/**
 * bbv-mega-menu.js
 * Beyond by Vera — desktop mega-menu enhancement.
 *
 * AGENTS.md compliance:
 * - Fully wrapped in an IIFE — zero global variables.
 * - All config dynamically read from <script id="bbv-mega-menu-config" type="application/json">
 *   injected by snippets/bbv-mega-menu-config.liquid.
 * - All DOM queries use data-bbv-* attributes — no CSS class selectors.
 * - Business logic (card building, config grouping) is pure and DOM-free.
 * - DOM logic only reads inputs and applies outputs.
 * - Defensive coding with try/catch guards.
 * - Full support for Shopify Theme Customizer live section/block reloads.
 */
(function () {
  'use strict';

  /* ─── Constants ──────────────────────────────────────────────────────────*/
  var BUILT_FLAG    = 'bbvNavBuilt';
  var HEADING_ATTR  = 'data-bbv-heading';
  var HEADING_CLASS = 'bbv-menu-heading';
  var LABEL_CLASS   = 'nav-link-text';
  var WAIT_TIMEOUT  = 20000;
  var MENU_VIDEO_SRC_ATTR = 'data-bbv-video-src';

  /* Column relocations — empty when using Shopify native sub-menus */
  var MENU_MOVES = [];

  /* ─── data-* selectors (AGENTS.md: no CSS class selectors in JS) ─────── */
  var SEL = {
    navRoot:       '[data-bbv-nav-root]',
    linkContainer: '[data-bbv-link-container]',
    mainLink:      '[data-bbv-main-link]',
    dropdown:      '[data-bbv-dropdown]',
    menuContainer: '[data-bbv-menu-container]',
    dropdownList:  '[data-bbv-dropdown-list]',
    navWrap:       '[data-bbv-nav-wrap]',
    menuImages:    '[data-bbv-menu-images]',
    arrow:         '[data-bbv-arrow]',
  };

  /* ─── Static HTML fragments ───────────────────────────────────────────── */
  var SALE_DROPDOWN_HTML = [
    '<div data-bbv-dropdown data-menu="sale"',
    '     class="animate__animated animate__fadeIn nav-menu-js single-dropdown main-menu--sticky-true child-link">',
    '  <div data-bbv-menu-container class="single-menu-container">',
    '    <ul data-bbv-dropdown-list class="site-nav__dropdown" role="menu">',
    '      <div data-bbv-nav-wrap class="main-nav-wrap">',
    '        <li class="child-site-nav__link site-nav__link child-div children-4" role="menuitem">',
    '          <a class="site-nav__link" href="/collections/sale">All Sale Products</a>',
    '        </li>',
    '        <li class="child-site-nav__link site-nav__link child-div children-4" role="menuitem">',
    '          <a class="site-nav__link" href="/collections/finds-under-100">Under $100</a>',
    '        </li>',
    '        <li class="child-site-nav__link site-nav__link child-div children-4" role="menuitem">',
    '          <a class="site-nav__link" href="/collections/finds-under-200">Under $200</a>',
    '        </li>',
    '        <li class="child-site-nav__link site-nav__link child-div children-4" role="menuitem">',
    '          <a class="site-nav__link" href="/collections/finds-under-250">Under $250</a>',
    '        </li>',
    '      </div>',
    '    </ul>',
    '  </div>',
    '</div>',
  ].join('\n');

  var DROP_ICON_HTML =
    '<span data-bbv-arrow class="site-nav__arrow-down" aria-hidden="true">' +
    '<svg width="16px" height="10px" viewBox="0 0 16 10" version="1.1"' +
    '     xmlns="http://www.w3.org/2000/svg">' +
    '<g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">' +
    '<g transform="translate(3,2)" fill="#000000" fill-rule="nonzero">' +
    '<polygon points="5 6 0.757 1.757 2.172 0.343 5 3.172 7.828 0.343 9.243 1.757"/>' +
    '</g></g></svg></span>';

  var ARROW_SVG =
    '<svg width="41" height="8" viewBox="0 0 41 8" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M40.3536 4.03554C40.5488 3.84028 40.5488 3.52369 40.3536 3.32843L37.1716 0.14645' +
    'C36.9763 -0.0488122 36.6597 -0.0488122 36.4645 0.14645C36.2692 0.341712 36.2692 0.658295' +
    ' 36.4645 0.853557L39.2929 3.68198L36.4645 6.51041C36.2692 6.70567 36.2692 7.02226 36.4645' +
    ' 7.21752C36.6597 7.41278 36.9763 7.41278 37.1716 7.21752L40.3536 4.03554Z' +
    'M0 3.68198V4.18198H40V3.68198V3.18198H0V3.68198Z" fill="black"/></svg>';

  /* ─── Config bootstrap ───────────────────────────────────────────────────*/
  function readConfig() {
    try {
      var el = document.getElementById('bbv-mega-menu-config');
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      warn('readConfig: JSON parse failed — ' + (e && e.message));
      return {};
    }
  }

  /* ─── Utils ──────────────────────────────────────────────────────────────*/
  function qs(selector, scope) {
    try {
      return (scope || document).querySelector(selector);
    } catch (e) {
      warn('Invalid selector: ' + selector);
      return null;
    }
  }

  function qsa(selector, scope) {
    try {
      return Array.prototype.slice.call(
        (scope || document).querySelectorAll(selector)
      );
    } catch (e) {
      warn('Invalid selector: ' + selector);
      return [];
    }
  }

  function childMatching(el, selector) {
    if (!el) return null;
    for (var i = 0; i < el.children.length; i++) {
      if (el.children[i].matches(selector)) return el.children[i];
    }
    return null;
  }

  function elementFromHTML(html) {
    var tmpl = document.createElement('template');
    tmpl.innerHTML = String(html).trim();
    return tmpl.content.firstElementChild;
  }

  function slugify(text) {
    return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function normalise(text) {
    return String(text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function warn(message) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[BBVMegaMenu] ' + message);
    }
  }

  /* ─── DOM readiness ───────────────────────────────────────────────────── */
  function waitUntil(predicate, onReady, onTimeout) {
    function isReady() {
      try { return !!predicate(); } catch (e) { return false; }
    }

    if (isReady()) { onReady(); return; }

    var finished = false;
    var observer = new MutationObserver(check);
    var timer = setTimeout(function () { finish(false); }, WAIT_TIMEOUT);

    observer.observe(document.documentElement, { childList: true, subtree: true });

    function check() {
      if (!finished && isReady()) finish(true);
    }

    function finish(isSuccess) {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      observer.disconnect();
      if (isSuccess) onReady();
      else if (typeof onTimeout === 'function') onTimeout();
    }
  }

  /* ─── Business logic (pure, DOM-free) ────────────────────────────────────*/
  function groupCardsByTab(cards) {
    var grouped = {};
    cards.forEach(function (card) {
      var tab = String(card.menuTab || '').toLowerCase();
      if (!tab) return;
      if (tab === 'discover') tab = 'collections';
      if (!grouped[tab]) grouped[tab] = [];
      grouped[tab].push(card);
    });
    return grouped;
  }

  function resolveCardLabel(card) {
    if (!card.label) return card.label;
    if (card.appendArrow) return card.label + ' ' + ARROW_SVG;
    return card.label;
  }

  /* ─── DOM: slug tagging ───────────────────────────────────────────────── */
  function tagMenusWithSlugs(nav) {
    qsa(SEL.linkContainer, nav).forEach(function (item) {
      var slug = item.getAttribute('data-bbv-menu');
      if (!slug) {
        var link = qs(SEL.mainLink, item);
        slug = slugify(link && link.textContent);
        if (slug) item.setAttribute('data-bbv-menu', slug);
      }
      if (!slug) return;

      qsa(SEL.dropdown, item).forEach(function (dropdown) {
        if (!dropdown.getAttribute('data-menu')) {
          dropdown.setAttribute('data-menu', slug);
        }
      });
    });
  }

  /* ─── DOM: sale dropdown ──────────────────────────────────────────────── */
  function buildSaleDropdown(nav, saleLinks) {
    if (!Array.isArray(saleLinks) || !saleLinks.length) return;

    var saleContainer = qs(SEL.linkContainer + '[data-bbv-menu="sale"]', nav);
    if (!saleContainer) return;

    var existingPanel = qs(SEL.dropdown + '[data-menu="sale"]', saleContainer);
    if (existingPanel) {
      var wrap = qs(SEL.navWrap, existingPanel);
      if (wrap) {
        wrap.innerHTML = saleLinks.map(function (item) {
          return '<li class="child-site-nav__link site-nav__link child-div children-4" role="menuitem">' +
            '<a class="site-nav__link" href="' + (item.href || '#') + '">' + (item.title || '') + '</a></li>';
        }).join('');
      }
      return;
    }

    var link = qs('a', saleContainer);

    saleContainer.classList.add('site-nav--has-dropdown');
    if (link) {
      link.classList.add(
        'site-nav--has-dropdown_link',
        'animate__animated',
        'animate__fadeIn'
      );
    }

    var linksHtml = saleLinks.map(function (item) {
      return '<li class="child-site-nav__link site-nav__link child-div children-4" role="menuitem">' +
        '<a class="site-nav__link" href="' + (item.href || '#') + '">' + (item.title || '') + '</a></li>';
    }).join('');

    var html = [
      '<div data-bbv-dropdown data-menu="sale"',
      '     class="animate__animated animate__fadeIn nav-menu-js single-dropdown main-menu--sticky-true child-link">',
      '  <div data-bbv-menu-container class="single-menu-container">',
      '    <ul data-bbv-dropdown-list class="site-nav__dropdown" role="menu">',
      '      <div data-bbv-nav-wrap class="main-nav-wrap">',
      linksHtml,
      '      </div>',
      '    </ul>',
      '  </div>',
      '</div>'
    ].join('\n');

    var panel = elementFromHTML(html);
    if (panel) saleContainer.appendChild(panel);

    if (link && !qs(SEL.arrow, link)) {
      var icon = elementFromHTML(DROP_ICON_HTML);
      if (icon) link.appendChild(icon);
    }
  }

  /* ─── DOM: section headings ───────────────────────────────────────────── */
  function addSectionHeadings(nav, menuHeadings) {
    Object.keys(menuHeadings).forEach(function (slug) {
      var headingText = menuHeadings[slug];
      if (!headingText) return;

      var headings = qsa('[' + HEADING_ATTR + '="' + slug + '"]', nav);
      if (headings.length) {
        headings.forEach(function (h) {
          var a = qs('a', h);
          if (a) a.textContent = headingText;
          else h.textContent = headingText;
        });
        return;
      }
    });
  }

  function dedupeHeadings(nav) {
    qsa(SEL.dropdown, nav).forEach(function (panel) {
      var menu = panel.closest(SEL.linkContainer);
      var link = menu && qs(SEL.mainLink, menu);
      var label = link ? normalise(link.textContent) : '';
      var seen = {};

      qsa('h1,h2,h3,h4,h5,h6', panel).forEach(function (heading) {
        var key = normalise(heading.textContent);
        if (!key) return;

        var isInjected = heading.hasAttribute(HEADING_ATTR);
        var isRepeat   = seen[key] === true || (!!label && key === label);

        if (isRepeat) {
          if (isInjected && heading.parentNode) heading.parentNode.removeChild(heading);
          return;
        }
        seen[key] = true;
      });
    });
  }

  /* ─── DOM: column relocation ─────────────────────────────────────────── */
  function relocateMenuColumns(nav) {
    MENU_MOVES.forEach(function (move) {
      var sources = qsa(
        SEL.linkContainer + '[data-bbv-menu="' + move.from + '"]' +
        ' > ' + SEL.dropdown + ' > ' + SEL.menuContainer,
        nav
      ).filter(function (source) {
        return !source.getAttribute('data-bbv-moved-from');
      });

      var target = qs(
        SEL.linkContainer + '[data-bbv-menu="' + move.to + '"]' +
        ' > ' + SEL.dropdown,
        nav
      );
      if (!sources.length || !target) return;

      sources.forEach(function (source) {
        if (source.parentNode === target) return;
        var anchor = childMatching(target, SEL.menuImages);
        target.insertBefore(source, anchor || null);
        source.setAttribute('data-bbv-moved-from', move.from);
      });
    });
  }

  /* ─── DOM: image containers ───────────────────────────────────────────── */
  function addImageContainers(nav) {
    qsa('.site-nav--has-dropdown ' + SEL.dropdown, nav).forEach(function (dropdown) {
      var container = childMatching(dropdown, SEL.menuImages);
      if (!container) {
        container = document.createElement('div');
        container.setAttribute('data-bbv-menu-images', '');
        container.className = 'menu-images bbv-menu-images';
      }
      if (dropdown.lastElementChild !== container) {
        dropdown.appendChild(container);
      }
    });
  }

  /* ─── Business: card building (pure) ──────────────────────────────────── */
  function buildVideoElement(url) {
    var video = document.createElement('video');
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('width', '100%');
    video.setAttribute('height', 'auto');
    video.style.display = 'block';
    video.style.objectFit = 'cover';
    // No src yet: the file is only requested when its menu opens (see loadMenuVideos).
    video.preload = 'none';
    video.setAttribute(MENU_VIDEO_SRC_ATTR, url);
    return video;
  }

  /* ─── DOM: start menu videos only when their menu is opened ──────────── */
  function loadMenuVideos(scope) {
    qsa('video[' + MENU_VIDEO_SRC_ATTR + ']', scope).forEach(function (video) {
      try {
        video.src = video.getAttribute(MENU_VIDEO_SRC_ATTR);
        video.removeAttribute(MENU_VIDEO_SRC_ATTR);
        var attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(function () {});
        }
      } catch (error) {
        warn('loadMenuVideos failed: ' + (error && error.message));
      }
    });
  }

  function buildImageElement(data) {
    var img = document.createElement('img');
    img.src = data.src;
    img.alt = data.alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    return img;
  }

  function buildTextBlock(className, value, isHTML) {
    var block = document.createElement('div');
    block.className = className;
    if (value == null) return block;
    if (isHTML) block.innerHTML = value;
    else block.textContent = value;
    return block;
  }

function buildMenuCard(data) {
  var card = document.createElement('div');
  card.className = 'menu-card bbv-menu-card';

  // full-cover click layer
  var link = document.createElement('a');
  link.className = 'bbv-menu-card__link';
  link.href = data.href || '#';
  link.setAttribute('aria-label', data.label || data.title || '');
  card.appendChild(link);

  if (data.videoUrl) {
    card.appendChild(buildVideoElement(data.videoUrl));
  } else if (data.src) {
    var media = document.createElement('div');
    media.className = 'bbv-menu-card__media';
    media.style.backgroundImage = 'url("' + data.src + '")';
    card.appendChild(media);
  }

  card.appendChild(buildTextBlock('menu-card-title bbv-menu-card__title', data.title));
  card.appendChild(buildTextBlock('menu-card-subtitle bbv-menu-card__subtitle', data.subtitle));
  card.appendChild(buildTextBlock('menu-card-label bbv-menu-card__label', data.label, true));

  return card;
}

  /* ─── DOM: render image cards ────────────────────────────────────────────*/
  function renderMenuImages(nav, cardsByTab) {
    Object.keys(cardsByTab).forEach(function (tab) {
      var cards  = cardsByTab[tab];
      var panel  = qs(SEL.dropdown + '[data-menu="' + tab + '"]', nav);
      if (!panel) return;

      var container = childMatching(panel, SEL.menuImages);
      if (!container) return;
      container.innerHTML = '';

      var fragment = document.createDocumentFragment();
      cards.forEach(function (card) {
        fragment.appendChild(buildMenuCard(card));
      });
      container.appendChild(fragment);
    });
  }

  /* ─── DOM: about-link descriptions ───────────────────────────────────── */
  /* ─── DOM: about-link descriptions ───────────────────────────────────── */
function addLinkDescriptions(nav, linkDescriptions) {
  linkDescriptions.forEach(function (entry) {
    if (!entry.hrefEndsWith || !entry.html) return;

    var selector =
      SEL.dropdown + ' ' + SEL.menuContainer + ' ' +
      SEL.navWrap + ' a[href$="' + entry.hrefEndsWith + '"]';

    qsa(selector, nav).forEach(function (link) {
      if (link.dataset.bbvDescDone) return;

      var href = link.getAttribute('href');
      var existingSpan = link.querySelector('.about-link-title, .bbv-about-link-title');
      var titleText = (entry.title && entry.title.trim()) ||
        (existingSpan ? existingSpan.textContent.trim() : link.textContent.trim());

      // wrapper replaces the <a> entirely
      var wrap = document.createElement('div');
      wrap.className = 'bbv-about-link';
      wrap.dataset.bbvDescDone = '1';

      var titleSpan = document.createElement('span');
      titleSpan.className = 'about-link-title bbv-about-link-title';
      titleSpan.textContent = titleText;
      wrap.appendChild(titleSpan);

      // description becomes the only clickable part
      var descLink = document.createElement('a');
      descLink.className = 'bbv-about-link-desc';
      descLink.href = href;
      descLink.innerHTML = entry.html;
      wrap.appendChild(descLink);

      link.parentNode.replaceChild(wrap, link);
    });
  });
}

  /* ─── DOM: menu label renames ─────────────────────────────────────────── */
  function ownTextNodes(el) {
    var nodes = [];
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === Node.TEXT_NODE) nodes.push(el.childNodes[i]);
    }
    return nodes;
  }

  function updateElementText(el, text) {
    if (!el) return;
    var labelSpan = childMatching(el, '.' + LABEL_CLASS) || qs('.' + LABEL_CLASS, el);
    if (labelSpan) {
      labelSpan.textContent = text;
      // Strip any duplicate stray text nodes outside the span
      ownTextNodes(el).forEach(function (n) {
        if (n.nodeValue.trim()) n.nodeValue = '';
      });
      return;
    }

    var textNodes = ownTextNodes(el);
    if (textNodes.length) {
      var found = false;
      textNodes.forEach(function (n) {
        if (!found && n.nodeValue.trim()) {
          n.nodeValue = text;
          found = true;
        } else if (n.nodeValue.trim()) {
          n.nodeValue = '';
        }
      });
      if (found) return;
    }

    var span = document.createElement('span');
    span.className = LABEL_CLASS;
    span.textContent = text;
    el.insertBefore(span, el.firstChild);
  }

  function applyMenuLabelRenames(nav, renames) {
    Object.keys(renames).forEach(function (slug) {
      var newLabel = renames[slug];
      var container = qs(SEL.linkContainer + '[data-bbv-menu="' + slug + '"]', nav);
      if (!container) return;
      var link = qs(SEL.mainLink, container);
      if (!link) return;
      updateElementText(link, newLabel);
    });
  }

  function applyHrefOverrides(nav, overrides) {
    Object.keys(overrides).forEach(function (slug) {
      var newHref = overrides[slug];
      var container = qs(SEL.linkContainer + '[data-bbv-menu="' + slug + '"]', nav);
      if (!container) return;
      var anchor = qs('[data-bbv-main-link]', container) || qs('a', container);
      if (anchor && anchor.getAttribute('href') !== newHref) {
        anchor.setAttribute('href', newHref);
      }
    });
  }

  function wrapNavLabels(scope) {
    qsa(SEL.linkContainer + ' ' + SEL.mainLink, scope).forEach(function (link) {
      if (childMatching(link, '.' + LABEL_CLASS)) return;
      ownTextNodes(link).forEach(function (node) {
        if (!node.nodeValue.trim()) return;
        var span = document.createElement('span');
        span.className = LABEL_CLASS;
        span.textContent = node.nodeValue.trim();
        node.parentNode.replaceChild(span, node);
      });
    });
  }

  function applyCssVariables(nav, accent, bg) {
    try {
      nav.style.setProperty('--bbv-accent-color', accent || '#c37899');
      nav.style.setProperty('--bbv-dropdown-bg', bg || '#fffaf5');
    } catch (e) {
      warn('applyCssVariables failed: ' + (e && e.message));
    }
  }

  /* ─── Orchestration ───────────────────────────────────────────────────── */
  function enhanceNav(nav, cfg) {
    if (!nav || nav.getAttribute('data-' + BUILT_FLAG) === 'true') return;
    nav.setAttribute('data-' + BUILT_FLAG, 'true');

    var cardsByTab = groupCardsByTab(
      (cfg.navCards || []).map(function (card) {
        return Object.assign({}, card, { label: resolveCardLabel(card) });
      })
    );

    applyCssVariables(nav, cfg.accentColor, cfg.dropdownBackground);
    tagMenusWithSlugs(nav);
    buildSaleDropdown(nav, cfg.saleLinks);
    relocateMenuColumns(nav);
    addSectionHeadings(nav, cfg.menuHeadings || {});
    addImageContainers(nav);
    renderMenuImages(nav, cardsByTab);
    addLinkDescriptions(nav, cfg.linkDescriptions || []);
    initHoverBridge(nav);
  }

  function initHoverBridge(nav) {
    patchThemeNavHover();
  }

    function patchThemeNavHover() {
    if (typeof window === 'undefined') return;

    /* Neutralise theme.Navigation — it owns nav-hover otherwise */
    function killThemeNav() {
      if (!window.theme || !window.theme.Navigation) return false;
      var N = window.theme.Navigation;
      if (N._bbvKilled) return true;
      N._bbvKilled = true;
      ['exitNav', 'closeNav', 'hideDropdown', 'resetNav'].forEach(function (fn) {
        if (typeof N[fn] === 'function') N[fn] = function () {};
      });
      return true;
    }
    if (!killThemeNav()) {
      var t = 0, poll = setInterval(function () {
        if (killThemeNav() || ++t > 50) clearInterval(poll);
      }, 100);
    }

    /* Strip theme's own hover bindings — it rebinds late */
    function stripThemeHandlers() {
      if (!window.jQuery) return;
      window.jQuery(
        '#AccessibleNav .site-nav--has-dropdown, ' +
        '.site-nav__link-container, .main-menu-dropdown, ' +
        '.single-dropdown, [data-bbv-dropdown]'
      ).off('mouseenter mouseleave mouseover mouseout');
    }
    [0, 300, 800, 1500, 3000].forEach(function (ms) { setTimeout(stripThemeHandlers, ms); });

    if (window._bbvHoverDelegated) return;
    window._bbvHoverDelegated = true;

    var ITEM  = '#AccessibleNav .site-nav--has-dropdown';
    var PANEL = '.main-menu-dropdown, .single-dropdown, [data-bbv-dropdown]';
    var CLOSE_DELAY = 300;
    var OUTSIDE_DELAY = 300;
    var closeTimer = null;

    function cancelClose() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    function closeAll() {
      qsa(ITEM + '.nav-hover').forEach(function (el) {
        el.classList.remove('nav-hover');
      });
    }

    function openOnly(item) {
      qsa(ITEM + '.nav-hover').forEach(function (el) {
        if (el !== item) el.classList.remove('nav-hover');
      });
      item.classList.add('nav-hover');
      loadMenuVideos(item);
    }

    function scheduleClose(delay) {
      cancelClose();
      closeTimer = setTimeout(function () {
        if (!document.querySelector(ITEM + ':hover') &&
            !document.querySelector('.main-menu-dropdown:hover') &&
            !document.querySelector('.single-dropdown:hover')) {
          closeAll();
        }
      }, delay);
    }

    document.addEventListener('mouseover', function (e) {
      if (!e.target || !e.target.closest) return;

      var item = e.target.closest(ITEM);
      if (item) { cancelClose(); openOnly(item); return; }

      var panel = e.target.closest(PANEL);
      if (panel) {
        cancelClose();
        var owner = panel.closest(ITEM);
        if (owner) openOnly(owner);
        return;
      }

      if (!e.target.closest('#header')) scheduleClose(OUTSIDE_DELAY);   // was closeAll()
    }, true);

    /* Keyboard users open menus by focus: start their videos as well */
    document.addEventListener('focusin', function (e) {
      if (!e.target || !e.target.closest) return;
      var item = e.target.closest(ITEM);
      if (item) loadMenuVideos(item);
    }, true);

    document.addEventListener('mouseout', function (e) {
      if (!e.target || !e.target.closest) return;
      if (!e.target.closest(ITEM) && !e.target.closest(PANEL)) return;

      var to = e.relatedTarget;
      if (to && to.closest && (to.closest(ITEM) || to.closest(PANEL))) return;

      scheduleClose(CLOSE_DELAY);
    }, true);
  }
  function init(force) {
    var rawCfg = readConfig();
    var cfg = {
      navCards:           Array.isArray(rawCfg.navData) ? rawCfg.navData : [],
      saleLinks:          Array.isArray(rawCfg.saleLinks) ? rawCfg.saleLinks : [],
      menuHeadings:       rawCfg.menuHeadings && typeof rawCfg.menuHeadings === 'object' ? rawCfg.menuHeadings : {},
      linkDescriptions:   Array.isArray(rawCfg.linkDescriptions) ? rawCfg.linkDescriptions : [],
      menuLabelRenames:   rawCfg.menuLabelRenames && typeof rawCfg.menuLabelRenames === 'object' ? rawCfg.menuLabelRenames : {},
      hrefOverrides:      rawCfg.hrefOverrides && typeof rawCfg.hrefOverrides === 'object' ? rawCfg.hrefOverrides : {},
      accentColor:        typeof rawCfg.accentColor === 'string' ? rawCfg.accentColor : '#c37899',
      dropdownBackground: typeof rawCfg.dropdownBackground === 'string' ? rawCfg.dropdownBackground : '#fffaf5',
    };

    var navs = qsa(SEL.navRoot);
    if (!navs.length) return false;

    navs.forEach(function (nav) {
      if (force) nav.removeAttribute('data-' + BUILT_FLAG);
      try {
        enhanceNav(nav, cfg);
      } catch (error) {
        warn('Nav build failed: ' + (error && error.message));
      }
    });

    navs.forEach(function (nav) {
      try {
        applyMenuLabelRenames(nav, cfg.menuLabelRenames);
        applyHrefOverrides(nav, cfg.hrefOverrides);
      } catch (error) {
        warn('Label/href update failed: ' + (error && error.message));
      }
    });

    navs.forEach(function (nav) {
      try {
        dedupeHeadings(nav);
      } catch (error) {
        warn('Heading dedupe failed: ' + (error && error.message));
      }
    });

    wrapNavLabels();
    try { patchThemeNavHover(); } catch (e) { warn('hover patch failed: ' + (e && e.message)); }
    return true;
  }

  /* ─── Initialise ──────────────────────────────────────────────────────── */
  waitUntil(
    function () { return document.querySelector(SEL.navRoot); },
    function () { init(false); },
    function () { warn('Timed out waiting for nav root element.'); }
  );

  /* ─── Shopify Theme Editor Lifecycle Events ──────────────────────────── */
  ['shopify:section:load', 'shopify:section:select', 'shopify:section:deselect', 'shopify:block:select', 'shopify:block:deselect'].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      init(true);
    });
  });

})();
